// Compact operational client card rendered as a raw Leaflet popup string.
import type { ClienteMapPoint } from '@aid/shared';
import { formatCurrency } from '../../../lib/format';
import { DATE_LOCALE } from '../constants';
import type { DashboardT } from '../dashboard-captions';

export interface ClientesMapPopupTheme {
  labelColor: string;
  textColor: string;
  mutedColor: string;
  borderColor: string;
  elevatedColor: string;
  primaryColor: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
}

interface FinancialState {
  label: string;
  color: string;
  amount: number;
  amountLabel: string;
  detail: string;
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function toAmount(value: string | null | undefined): number {
  const amount = Number.parseFloat(value ?? '0');
  return Number.isFinite(amount) ? amount : 0;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return escapeHtml(iso);
  return escapeHtml(
    d.toLocaleDateString(DATE_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' }),
  );
}

function formatPeriod(start: string | null | undefined, end: string | null | undefined): string {
  const from = formatDate(start);
  const to = formatDate(end);
  if (from === '—') return to;
  if (to === '—') return from;
  return `${from} — ${to}`;
}

function financialState(
  point: ClienteMapPoint,
  t: DashboardT,
  theme: ClientesMapPopupTheme,
): FinancialState {
  const hasPortfolioMetrics = point.deudaPendiente != null || point.saldoAFavor != null;
  const fallbackInitialBalance = toAmount(point.saldoInicial);
  const debt = hasPortfolioMetrics
    ? toAmount(point.deudaPendiente)
    : Math.max(-fallbackInitialBalance, 0);
  const credit = hasPortfolioMetrics
    ? toAmount(point.saldoAFavor)
    : Math.max(fallbackInitialBalance, 0);
  const overdue = point.facturasVencidas ?? 0;
  const daysLate = point.diasEnMora ?? 0;

  if (debt > 0 && overdue > 0) {
    return {
      label: t('dashboard.mapaPopupEstadoMora'),
      color: theme.errorColor,
      amount: debt,
      amountLabel: t('dashboard.mapaPopupSaldoPendiente'),
      detail: `${overdue} ${t('dashboard.mapaPopupFacturasVencidas')}${daysLate > 0 ? ` · ${daysLate} ${t('dashboard.mapaPopupDiasMora')}` : ''}`,
    };
  }

  if (debt > 0) {
    return {
      label: t('dashboard.mapaPopupEstadoPendiente'),
      color: theme.warningColor,
      amount: debt,
      amountLabel: t('dashboard.mapaPopupSaldoPendiente'),
      detail: t('dashboard.mapaPopupSaldoPendienteDetalle'),
    };
  }

  if (credit > 0) {
    return {
      label: t('dashboard.mapaPopupEstadoSaldoAFavor'),
      color: theme.primaryColor,
      amount: credit,
      amountLabel: t('dashboard.mapaPopupSaldoAFavor'),
      detail: t('dashboard.mapaPopupSaldoAFavorDetalle'),
    };
  }

  return {
    label: t('dashboard.mapaPopupEstadoAlDia'),
    color: theme.successColor,
    amount: 0,
    amountLabel: t('dashboard.mapaPopupSaldoPendiente'),
    detail: t('dashboard.mapaPopupSinSaldoPendiente'),
  };
}

function detailRow(label: string, value: string, theme: ClientesMapPopupTheme): string {
  return `<div style="display:flex;justify-content:space-between;gap:12px;padding:4px 0;">
    <span style="color:${theme.labelColor};">${escapeHtml(label)}</span>
    <strong style="color:${theme.textColor};text-align:right;font-weight:600;">${value}</strong>
  </div>`;
}

export function buildClientesMapPopup(
  point: ClienteMapPoint,
  t: DashboardT,
  theme: ClientesMapPopupTheme,
): string {
  const state = financialState(point, t, theme);
  const overdue = point.facturasVencidas;
  const billingDay = point.diaCobro == null ? '—' : String(point.diaCobro);
  const subscription = point.tipoSuscripcionValue ?? point.tipoSuscripcionKey ?? '—';
  const latestPayment = point.valor != null ? formatCurrency(String(point.valor)) : '—';
  const clientPath = `/clientes/${encodeURIComponent(String(point.id))}`;

  return `
    <div style="width:min(300px,calc(100vw - 48px));box-sizing:border-box;padding:12px 14px 14px;font-family:ui-sans-serif,system-ui,sans-serif;color:${theme.textColor};font-size:12px;line-height:1.45;">
      <div style="padding-bottom:10px;border-bottom:1px solid ${theme.borderColor};">
        <div style="font-size:15px;font-weight:700;line-height:1.25;overflow-wrap:anywhere;">${escapeHtml(point.nombre)}</div>
        <div style="margin-top:3px;color:${theme.mutedColor};font-size:11px;">${escapeHtml(t('dashboard.mapaPopupCodigo'))}: ${escapeHtml(point.ipCliente ?? '—')}</div>
      </div>

      <div style="margin:12px 0;padding:10px 12px;border:1px solid ${theme.borderColor};border-radius:10px;background:${theme.elevatedColor};">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;">
          <span style="display:inline-flex;align-items:center;gap:6px;color:${state.color};font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.04em;">
            <span style="width:8px;height:8px;border-radius:50%;background:${state.color};display:inline-block;"></span>
            ${escapeHtml(state.label)}
          </span>
          <strong style="font-size:18px;line-height:1.1;color:${theme.textColor};font-variant-numeric:tabular-nums;white-space:nowrap;">${escapeHtml(formatCurrency(String(state.amount)))}</strong>
        </div>
        <div style="display:flex;justify-content:space-between;gap:12px;margin-top:3px;color:${theme.mutedColor};font-size:11px;">
          <span>${escapeHtml(state.detail)}</span>
          <span style="white-space:nowrap;">${escapeHtml(state.amountLabel)}</span>
        </div>
      </div>

      <div style="border-bottom:1px solid ${theme.borderColor};padding-bottom:8px;">
        ${detailRow(t('dashboard.mapaPopupDiaCobro'), escapeHtml(billingDay), theme)}
        ${detailRow(t('dashboard.mapaPopupSuscripcion'), escapeHtml(subscription), theme)}
        ${overdue == null ? '' : detailRow(t('dashboard.mapaPopupFacturasVencidas'), escapeHtml(String(overdue)), theme)}
        ${detailRow(t('dashboard.mapaPopupPago'), escapeHtml(latestPayment), theme)}
        ${detailRow(t('dashboard.mapaPopupPeriodo'), formatPeriod(point.fechaInicio, point.fechaFin), theme)}
      </div>

      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding-top:10px;">
        <span style="color:${theme.labelColor};">${escapeHtml(t('dashboard.mapaPopupEstadoUltimoPago'))}: <strong style="color:${theme.textColor};">${escapeHtml(point.estadoPago ?? '—')}</strong></span>
        <a href="${clientPath}" style="color:${theme.primaryColor};font-weight:600;text-decoration:none;white-space:nowrap;">${escapeHtml(t('dashboard.mapaPopupVerCliente'))}</a>
      </div>
    </div>`;
}
